"use client";

import { useState } from "react";

import { TermsVersionSelect } from "@/components/terms-version-select";

import { getCurrentVersion, getVersionList } from "@/lib/terms-version";

import type { TableProps, TermsProps } from "@/dummy/terms/service-terms";

interface TermsDetailProps {
  terms: TermsProps;
  /** URL 쿼리 등으로 지정한 초기 버전. 없거나 존재하지 않는 버전이면 현행 버전을 쓴다 */
  initialVersion?: string;
}

/** 약관 본문 + 개정 이력 셀렉트. 약관 페이지와 약관 목록 탭에서 공통으로 사용 */
export function TermsDetail({ terms, initialVersion }: TermsDetailProps) {
  const versionList = getVersionList(terms);
  const currentVersion = getCurrentVersion(terms);

  const [selectedVersion, setSelectedVersion] = useState(
    initialVersion && versionList.includes(initialVersion) ? initialVersion : currentVersion
  );

  const shownVersion = terms.versions.find(version => version.version === selectedVersion);
  const itemsToShow = shownVersion?.sections ?? [];
  const description = shownVersion?.description ?? terms.description;

  return (
    <>
      <header className="mb-4">
        <div className="mb-8 flex items-center justify-between gap-6">
          <h2 className="text-2xl font-semibold text-neutral-800">{terms.title}</h2>
          <TermsVersionSelect
            versionList={versionList}
            currentVersion={currentVersion}
            value={selectedVersion}
            onChange={setSelectedVersion}
            className="min-w-44"
          />
        </div>
        {description && (
          <div className="text-neutral-600">
            <p>{description}</p>
          </div>
        )}
      </header>

      <div className="space-y-11">
        {itemsToShow.map((term, idx) => (
          <div key={`${selectedVersion}-${term.heading ?? idx}`} className="space-y-4">
            {term.heading && (
              <h3 className="text-lg font-semibold text-neutral-800">{term.heading}</h3>
            )}
            <div className="space-y-1 text-neutral-600">
              {term.content.map((contentItem, contentIdx) =>
                typeof contentItem === "string" ? (
                  <p key={contentIdx}>{contentItem}</p>
                ) : (
                  <TermsTable key={contentIdx} table={contentItem} />
                )
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function TermsTable({ table }: { table: TableProps }) {
  return (
    <div className="overflow-x-auto py-2">
      <table className="min-w-full divide-y divide-gray-200 border text-xs">
        <thead className="bg-gray-50">
          <tr>
            {table.headers.map(header => (
              <th
                key={header}
                className="border px-2 py-1 text-left font-medium whitespace-nowrap text-gray-500"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {table.rows.map((row, rIdx) => (
            <tr key={rIdx}>
              {row.map((cell, cIdx) => (
                <td key={cIdx} className="border px-2 py-1 whitespace-pre-wrap text-gray-500">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
